export const QUIET_AFTER_MS = 3 * 60 * 60_000;

export type QuietRoomKind = "active" | "focus-empty" | "quiet-empty" | "quiet-history";

export type QuietRoomPlan = {
  kind: QuietRoomKind;
  title: string;
  description: string;
  actionLabel: string;
  historyLabel: string | null;
  shouldLoadWall: boolean;
};

type QuietRoomPlanInput = {
  roomId: "nz-lobby" | "reo-maori";
  now: number;
  visibleMessageTimes: readonly number[];
  hasPending: boolean;
  focusEnabled: boolean;
  isReturning: boolean;
  viewerSignedIn: boolean;
};

function dateKey(timestamp: number) {
  return new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(timestamp);
}

function historyLabel(latestMessageAt: number, now: number) {
  return dateKey(latestMessageAt) === dateKey(now) ? "Earlier today" : "Recent room history";
}

export function quietRoomPlan(input: QuietRoomPlanInput): QuietRoomPlan {
  const fallback: QuietRoomPlan = {
    kind: "active",
    title: "",
    description: "",
    actionLabel: "",
    historyLabel: null,
    shouldLoadWall: false,
  };

  if (input.hasPending || input.isReturning) return fallback;
  if (input.focusEnabled) {
    return input.visibleMessageTimes.length
      ? fallback
      : { ...fallback, kind: "focus-empty", description: "Focus view is hiding messages from people outside your list." };
  }

  const validTimes = input.visibleMessageTimes.filter((timestamp) => Number.isFinite(timestamp) && timestamp > 0);
  const latestMessageAt = validTimes.length ? Math.max(...validTimes) : null;
  if (latestMessageAt !== null && Math.max(0, input.now - latestMessageAt) < QUIET_AFTER_MS) return fallback;

  const hasHistory = latestMessageAt !== null;
  const isReoRoom = input.roomId === "reo-maori";
  return {
    kind: hasHistory ? "quiet-history" : "quiet-empty",
    title: isReoRoom ? "Kei te mārie te rūma." : "The lobby is between conversations.",
    description: hasHistory
      ? "Start the next conversation whenever you are ready."
      : "No conversation has started yet. Leave the first line, or check back when someone else arrives.",
    actionLabel: input.viewerSignedIn
      ? isReoRoom ? "Start the kōrero" : "Start a conversation"
      : isReoRoom ? "Join the kōrero" : "Join as guest",
    historyLabel: hasHistory ? historyLabel(latestMessageAt, input.now) : null,
    shouldLoadWall: true,
  };
}
